/**
 * WHEN the rail's icon row may act (fix D / R2) — one pure selector, because
 * three of the four tiles ask the same two questions and a second copy of the
 * fair-play rule is the one thing in this task that must not drift.
 *
 * `liveGame` and `liveWatch` are the two "something is still being PLAYED on
 * this board" cases: you cannot walk back to the lobby out of either, and the
 * engine must not be available in either (R2's Analysis gate).
 *
 * A REPLAY (`watch.replayGameId`), an ENDED watch and a watch that OPENED on a
 * finished table (`WATCH_FINISHED_STATE`, fix AC) are finished games, so all
 * three allow everything a result of my own allows.
 */
import type {LiveGameState} from '@/Store/liveGameStore';
import {selectGameOver} from '@/Store/liveGame/selectors';
import {WATCH_FINISHED_STATE} from '@/Store/liveGame/state';

export type ActionGate = {
  /** a game of MINE is on the board and has not ended */
  liveGame: boolean;
  /** someone else's game is on the board and has not ended */
  liveWatch: boolean;
  /** a watch or a replay owns the board (either has to be left first) */
  watching: boolean;
  /** my own finished game, with a uuid to review */
  gameUuid: string;
  /** 'Abort' has nothing to review (the old screen's own rule) */
  aborted: boolean;
};

export const selectActionGate = (s: LiveGameState): ActionGate => {
  const watching = s.watch.active || !!s.watch.replayGameId;
  return {
    liveGame:
      !watching && (s.game.phase === 'playing' || s.game.phase === 'starting'),
    // fix AC (owner 2026-10-07, "analyse finished Swiss games inline like the
    // arena"): a finished table opened over the socket (a Swiss game within
    // the redis window) arrives with `state` 8 and NO end message, so `ended`
    // stays null — it is still not being played. The arena's finished games
    // take the HTTP replay path (`active: false`) and never hit this.
    liveWatch:
      s.watch.active &&
      !s.watch.ended &&
      s.watch.state !== WATCH_FINISHED_STATE,
    watching,
    gameUuid: s.game.result?.gameUuid || '',
    aborted: s.game.result?.outcome === 'Abort',
  };
};

/** nothing that is still being played is on the board. */
export const settled = (gate: ActionGate): boolean =>
  !gate.liveGame && !gate.liveWatch;

/** my own game is over and has something to look at (review / analyse). */
export const reviewable = (s: LiveGameState): boolean =>
  selectGameOver(s) && !!s.game.result?.gameUuid;
